/* ========================================================================
    Module: Switch forms
 ========================================================================== */
import { AxiosResponse, AxiosError } from "axios";
import { appAxios } from "bootstrap";
import {
    corporateSupportingMemberOrganizationSelectOptions,
    regularMemberOrganizationSelectOptions,
} from "~modules/organization-select-option";
import {
    CorporateSupportingMemberSelectOptionOrganizationJsonType,
    RegularMemberSelectOptionOrganizationJsonType,
} from "~types/organization-select-option";

const switchFormsByMemberType = (): void => {
    const memberTypeElements = document.querySelectorAll<HTMLInputElement>(
        ".js-form-member-type",
    );

    if (memberTypeElements.length <= 0) {
        return;
    }

    let foundChecked = false;
    memberTypeElements.forEach((element) => {
        if (element.checked) {
            switchDisplayForm(element);

            foundChecked = true;
        }

        element.addEventListener("change", (e) => {
            if (!(e.currentTarget instanceof HTMLInputElement)) {
                return;
            }

            switchDisplayForm(e.currentTarget);
        });
    });

    if (!foundChecked) {
        // 全ての操作要素を非表示にして、内包するフォーム要素を無効化する
        itemVisibility("js-form-visibility", false);
    }
};

/**
 * フォームを切り替えて表示する
 *
 * @param {HTMLInputElement} element
 * @returns {void}
 */
const switchDisplayForm = (element: HTMLInputElement): void => {
    // 全ての操作要素を非表示にして、内包するフォーム要素を無効化する
    itemVisibility("js-form-visibility", false);

    const target = element.dataset.target;

    if (!target) {
        return;
    }

    switch (target) {
        case "regular-member":
            regularMember();
            break;
        case "corporate-supporting-member":
            corporateSupportingMember();
            break;
        case "individual-supporting-member":
            individualSupportingMember();
            break;
        case "non-member":
            nonMember();
            break;
    }
};

const regularMember = (): void => {
    const organizationElement = document.querySelector<HTMLSelectElement>(
        ".js-form-organization",
    );

    if (!organizationElement) {
        return;
    }

    let oldValue: null | string = null;
    if (organizationElement.dataset.organizationNameSelectOld) {
        oldValue = organizationElement.dataset.organizationNameSelectOld;
    }

    itemVisibility("js-form-regular-member", true);

    organizationElement.disabled = true;

    appAxios
        .get("/membership/api/members/regular-members/select-options")
        .then(
            (
                response: AxiosResponse<RegularMemberSelectOptionOrganizationJsonType>,
            ) => {
                const organizationOptions = response.data.data;

                let options =
                    '<option value="">組織・機関名をお選びください</option>';

                if (organizationOptions.length <= 0) {
                    organizationElement.innerHTML = options;
                    return;
                }

                // 組織・機関名のドロップダウンの選択項目を生成する
                options += regularMemberOrganizationSelectOptions(
                    organizationOptions,
                    oldValue,
                );

                organizationElement.innerHTML = options;
            },
        )
        .catch((errors: AxiosError) => {
            console.log(errors.message);
        })
        .finally(() => {
            organizationElement.disabled = false;
        });
};

const corporateSupportingMember = (): void => {
    const organizationElement = document.querySelector<HTMLSelectElement>(
        ".js-form-organization",
    );

    if (!organizationElement) {
        return;
    }

    let oldValue: null | string = null;
    if (organizationElement.dataset.organizationNameSelectOld) {
        oldValue = organizationElement.dataset.organizationNameSelectOld;
    }

    itemVisibility("js-form-corporate-support-member", true);

    organizationElement.disabled = true;

    appAxios
        .get(
            "/membership/api/members/corporate-supporting-members/select-options",
        )
        .then(
            (
                response: AxiosResponse<CorporateSupportingMemberSelectOptionOrganizationJsonType>,
            ) => {
                const organizationOptions = response.data.data;

                let options =
                    '<option value="">組織・機関名をお選びください</option>';

                if (organizationOptions.length <= 0) {
                    organizationElement.innerHTML = options;
                    return;
                }

                // 組織・機関名のドロップダウンの選択項目を生成する
                options += corporateSupportingMemberOrganizationSelectOptions(
                    organizationOptions,
                    oldValue,
                );

                organizationElement.innerHTML = options;
            },
        )
        .catch((errors: AxiosError) => {
            console.log(errors.message);
        })
        .finally(() => {
            organizationElement.disabled = false;
        });
};

const individualSupportingMember = (): void => {
    itemVisibility("js-form-individual-supporting-member", true);
};

const nonMember = (): void => {
    itemVisibility("js-form-non-member", true);
};

/**
 * 項目の表示／非表示を切り替えて、内包するフォーム要素を有効／無効化する
 *
 * @param {string} className
 * @param {boolean} isVisible
 * @returns {void}
 */
const itemVisibility = (className: string, isVisible: boolean): void => {
    const visibilityElements = document.querySelectorAll<HTMLElement>(
        `.${className}`,
    );

    if (visibilityElements.length > 0) {
        visibilityElements.forEach((element) => {
            if (isVisible) {
                if (element instanceof HTMLTableRowElement) {
                    element.style.display = "table-row";
                } else {
                    element.style.display = "block";
                }
            } else {
                element.style.display = "none";
            }

            const formControlsElements = element.querySelectorAll<
                | HTMLInputElement
                | HTMLSelectElement
                | HTMLTextAreaElement
                | HTMLButtonElement
            >("input, select, textarea, button");

            if (formControlsElements.length < 0) {
                return;
            }

            formControlsElements.forEach((element) => {
                element.disabled = !isVisible;
            });
        });
    }
};

export default switchFormsByMemberType;
